import { SLOT_MINUTES, minutesSinceLocalMidnight } from '@fleet/shared';

/**
 * Umrechnung zwischen Zeit und Bildschirmposition.
 *
 * Bewusst als eigene, reine Funktionen und nicht in der Komponente: Diese
 * Rechnungen sind die häufigste Fehlerquelle einer Kalenderansicht — ein Block,
 * der eine Viertelstunde zu tief sitzt, fällt visuell kaum auf, führt aber
 * beim Ziehen zur falschen Buchungszeit. Getrennt sind sie testbar.
 */

export const TIMEZONE = 'Europe/Berlin';

/** Höhe eines 15-Minuten-Slots in Pixeln. */
export const SLOT_HEIGHT = 14;
/** Erste angezeigte Stunde. Nachts wird selten disponiert. */
export const DAY_START_HOUR = 6;
export const DAY_END_HOUR = 22;

export const VISIBLE_MINUTES = (DAY_END_HOUR - DAY_START_HOUR) * 60;
export const SLOTS_PER_HOUR = 60 / SLOT_MINUTES;
export const GRID_HEIGHT = (VISIBLE_MINUTES / SLOT_MINUTES) * SLOT_HEIGHT;

/** Minuten seit Beginn der Anzeige — kann negativ sein oder darüber hinausgehen. */
export function minutesFromDayStart(instant: Date): number {
  return minutesSinceLocalMidnight(instant, TIMEZONE) - DAY_START_HOUR * 60;
}

export interface BlockGeometry {
  top: number;
  height: number;
  /** Die Buchung beginnt vor dem sichtbaren Bereich oder an einem früheren Tag. */
  clippedTop: boolean;
  clippedBottom: boolean;
}

/**
 * Position und Höhe eines Buchungsblocks.
 *
 * Mehrtägige Buchungen und solche außerhalb der Anzeigezeiten werden
 * beschnitten und markiert, statt sie aus dem Raster laufen zu lassen — sonst
 * überlagerten sie benachbarte Spalten oder verschwänden ganz.
 */
export function blockGeometry(start: Date, end: Date, day: Date): BlockGeometry {
  const dayKey = localDateKey(day);

  // Beginnt die Buchung an einem früheren Tag, startet ihr sichtbarer Teil oben.
  const startsBefore = localDateKey(start) < dayKey;
  const endsAfter = localDateKey(end) > dayKey;

  const rawTop = startsBefore ? 0 : minutesFromDayStart(start);
  const rawBottom = endsAfter ? VISIBLE_MINUTES : minutesFromDayStart(end);

  const top = Math.max(0, rawTop);
  const bottom = Math.min(VISIBLE_MINUTES, rawBottom);

  return {
    top: (top / SLOT_MINUTES) * SLOT_HEIGHT,
    // Mindesthöhe, damit eine 15-Minuten-Buchung noch anklickbar bleibt.
    height: Math.max(SLOT_HEIGHT, ((bottom - top) / SLOT_MINUTES) * SLOT_HEIGHT),
    clippedTop: startsBefore || rawTop < 0,
    clippedBottom: endsAfter || rawBottom > VISIBLE_MINUTES,
  };
}

/** Aus einer Pixelposition den zugehörigen Slot-Index ableiten. */
export function slotIndexFromOffset(offsetY: number): number {
  const index = Math.floor(offsetY / SLOT_HEIGHT);
  return Math.max(0, Math.min(VISIBLE_MINUTES / SLOT_MINUTES - 1, index));
}

/** Slot-Index zurück in einen Zeitpunkt des angezeigten Tages. */
export function instantFromSlotIndex(day: Date, slotIndex: number): Date {
  const result = new Date(day);
  result.setHours(DAY_START_HOUR, 0, 0, 0);
  result.setMinutes(result.getMinutes() + slotIndex * SLOT_MINUTES);
  return result;
}

/** Lokaler Kalendertag als 'YYYY-MM-DD' — für Vergleiche über Tagesgrenzen. */
export function localDateKey(instant: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant);
}

/** Beschriftungen der Stundenzeile. */
export function hourLabels(): { hour: number; top: number }[] {
  const labels: { hour: number; top: number }[] = [];
  for (let hour = DAY_START_HOUR; hour <= DAY_END_HOUR; hour++) {
    labels.push({
      hour,
      top: ((hour - DAY_START_HOUR) * 60 * SLOT_HEIGHT) / SLOT_MINUTES,
    });
  }
  return labels;
}

export function formatTime(instant: Date): string {
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
  }).format(instant);
}

export function formatDay(instant: Date): string {
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: TIMEZONE,
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(instant);
}

/** Grenzen des angezeigten Tages als UTC-Zeitpunkte für die API-Abfrage. */
export function dayRange(day: Date): { from: Date; to: Date } {
  const from = new Date(day);
  from.setHours(0, 0, 0, 0);
  const to = new Date(from);
  to.setDate(to.getDate() + 1);
  return { from, to };
}

/**
 * Montag der Woche, in der dieser Tag liegt.
 *
 * `getDay()` zählt ab Sonntag — hierzulande beginnt die Woche am Montag, und
 * ein Fuhrpark wird nach Arbeitswochen disponiert, nicht nach Kalenderwochen
 * amerikanischer Zählung.
 */
export function startOfWeek(day: Date): Date {
  const result = new Date(day);
  result.setHours(0, 0, 0, 0);
  const weekday = (result.getDay() + 6) % 7;
  result.setDate(result.getDate() - weekday);
  return result;
}

/** Die sieben Tage der Woche, in der dieser Tag liegt. */
export function weekDays(day: Date): Date[] {
  const monday = startOfWeek(day);
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(monday);
    date.setDate(date.getDate() + index);
    return date;
  });
}

/** Grenzen der angezeigten Woche für die API-Abfrage. */
export function weekRange(day: Date): { from: Date; to: Date } {
  const from = startOfWeek(day);
  const to = new Date(from);
  to.setDate(to.getDate() + 7);
  return { from, to };
}

/**
 * Anteil eines Zeitraums am sichtbaren Tagesfenster, als Bruchteile 0…1.
 *
 * Für die Balken der Wochenansicht: Sie zeigen nicht die Uhrzeit auf die Minute,
 * sondern beantworten „wie voll ist dieser Tag" auf einen Blick. Was außerhalb
 * von 6 bis 22 Uhr liegt, wird auf die Ränder geklemmt statt aus dem Balken zu
 * laufen.
 */
export function dayFraction(start: Date, end: Date, day: Date): { left: number; width: number } {
  const dayKey = localDateKey(day);
  const startsBefore = localDateKey(start) < dayKey;
  const endsAfter = localDateKey(end) > dayKey;

  const rawFrom = startsBefore ? 0 : minutesFromDayStart(start);
  const rawTo = endsAfter ? VISIBLE_MINUTES : minutesFromDayStart(end);

  const from = Math.max(0, Math.min(VISIBLE_MINUTES, rawFrom));
  const to = Math.max(0, Math.min(VISIBLE_MINUTES, rawTo));

  return {
    left: from / VISIBLE_MINUTES,
    // Mindestbreite, damit eine Viertelstunde nicht unsichtbar wird.
    width: Math.max(0.02, (to - from) / VISIBLE_MINUTES),
  };
}

/**
 * Zeitangabe einer Buchung, bezogen auf einen bestimmten Tag.
 *
 * In der Wochenansicht erscheint eine mehrtägige Buchung in jeder betroffenen
 * Spalte. Dort überall die Startuhrzeit zu zeigen wäre irreführend: Am zweiten
 * Tag einer Buchung von Montag 9 Uhr bis Mittwoch 17 Uhr beginnt nichts um 9
 * Uhr — das Fahrzeug ist schlicht den ganzen Tag weg.
 */
export function dayTimeLabel(start: Date, end: Date, day: Date): string {
  const dayKey = localDateKey(day);
  const startsEarlier = localDateKey(start) < dayKey;
  const endsLater = localDateKey(end) > dayKey;

  if (startsEarlier && endsLater) return 'ganztägig';
  if (startsEarlier) return `bis ${formatTime(end)}`;
  if (endsLater) return `ab ${formatTime(start)}`;
  return `${formatTime(start)}–${formatTime(end)}`;
}

/**
 * Zeitraum einer Buchung, in einer Zeile.
 *
 * „Montag, 17.08.2026, 09:00 – 17:00" ist über Tagesgrenzen hinweg falsch: Das
 * liest sich wie ein Arbeitstag, während das Fahrzeug in Wahrheit bis Mittwoch
 * weg ist. Endet die Buchung an einem anderen Tag, steht dessen Datum deshalb
 * ausgeschrieben dabei — lieber lang als mehrdeutig, denn an dieser Angabe
 * hängt eine Freigabeentscheidung.
 */
export function formatRange(start: Date, end: Date): string {
  if (isSameDay(start, end)) {
    return `${formatDay(start)}, ${formatTime(start)} – ${formatTime(end)}`;
  }
  return `${formatDay(start)}, ${formatTime(start)} – ${formatDay(end)}, ${formatTime(end)}`;
}

/**
 * Nur die Uhrzeiten — für Stellen, an denen das Startdatum schon daneben steht.
 *
 * Beim Tageswechsel reicht das nicht, also kommt der Endtag in Kurzform dazu:
 * „09:00 – 19.08., 17:00".
 */
export function formatTimeRange(start: Date, end: Date): string {
  if (isSameDay(start, end)) return `${formatTime(start)} – ${formatTime(end)}`;
  return `${formatTime(start)} – ${formatDayMonth(end)}, ${formatTime(end)}`;
}

export function formatWeekday(instant: Date): string {
  return new Intl.DateTimeFormat('de-DE', { timeZone: TIMEZONE, weekday: 'short' }).format(instant);
}

export function formatDayMonth(instant: Date): string {
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: TIMEZONE,
    day: '2-digit',
    month: '2-digit',
  }).format(instant);
}

/** Liegt dieser Zeitpunkt am selben lokalen Kalendertag? */
export function isSameDay(a: Date, b: Date): boolean {
  return localDateKey(a) === localDateKey(b);
}
